import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { toastFactory } from '@/factories'
import { formatMoney } from '@/helpers/helpers'
import i18n from '@/i18n'
import {
  RefundState,
  RefundStateEnum,
} from '@/pages/reservations/reservations-dashboard/reservations-dashboard.constants'
import { resetQueries } from '@/utils'

import { RESERVATION_DETAILS_QUERY_KEY } from './get-reservation-details'
import { RESERVATION_TRANSACTIONS_QUERY_KEY } from './get-reservation-transactions'

export type CancelReservationRequest = {
  reservationId: number | string
  cancelNote?: string
  refundAmount?: string
  refundState: RefundState
}

const cancelReservation = async ({
  reservationId,
  cancelNote,
  refundAmount,
  refundState,
}: CancelReservationRequest) => {
  const status =
    refundState === RefundStateEnum.enum.refund || refundState === RefundStateEnum.enum.partial
      ? '1'
      : '0'

  const parsedRefundAmount = refundAmount ? parseFloat(refundAmount) : undefined
  const result = await authAPI.post<string>(`/reservation/${reservationId}/cancel/${status}`, {
    cancelNote,
    refundAmount: parsedRefundAmount,
  })

  return result.data
}

export const useCancelReservation = () => {
  return useMutation({
    mutationFn: cancelReservation,
    onError: () => {
      toastFactory.error(i18n.t('reservations:details.apiMessages.cancelError'))
    },
    onSuccess: async (_data, params) => {
      let message: string

      if (params.refundState === RefundStateEnum.enum.no_refund) {
        message = i18n.t('reservations:details.apiMessages.reservationCancelWithoutRefundSuccess')
      } else if (params.refundState === RefundStateEnum.enum.refund) {
        message = i18n.t(
          'reservations:details.apiMessages.reservationCancelWithFullRefundSuccess',
          {
            refundAmount: formatMoney(params.refundAmount),
          },
        )
      } else {
        message = i18n.t(
          'reservations:details.apiMessages.reservationCancelWithPartialRefundSuccess',
          {
            refundAmount: formatMoney(params.refundAmount),
          },
        )
      }

      await resetQueries([RESERVATION_DETAILS_QUERY_KEY], [RESERVATION_TRANSACTIONS_QUERY_KEY])
      toastFactory.success(message)
    },
  })
}
